import styled from 'styled-components';
import PropTypes from 'prop-types';
import { GridContainerWithBackground } from '@glowforge/gf-styled/components/grid-container';
import {
  Paragraph,
  Header4,
  getTypographyVariant,
  Title,
} from '@glowforge/gf-styled/components/typography/Typography';
import { Image } from '@glowforge/gf-styled/components/contentful-views';
import SVG from '@glowforge/gf-styled/components/svg';

export const PRO_PILL_HEIGHT = '50px';

// cant work with meet the family rebrand because relying on fixed specific height
export const CardWrapper = styled('div')`
  position: relative;
  display: block;
  // ensure Pro is first
  order: ${(props) => (props?.proPill ? 1 : 2)};

  @media ${(props) => props.theme.queries.desktopAndUp} {
    // all have same order, render in order they are declared on page
    order: 1;
    display: grid;
    /*
      Attempting to line up with bottom line of Glowforge.
      Natural image height is 622. The line is 368px from top
      of image. To have this style, needs consistency in image
      dimensions and layout.
     */
    grid-template-rows:
      [TopImage] ${(props) => ((props?.imgHeight ?? 0) * 368) / 622}px
      [CardStart] ${(props) => ((props?.imgHeight ?? 0) * 254) / 622}px
      [TopImageEnd] min-content
      [CardEnd];

    // pushes non-pro units down by pillHeight. This allows all content below pill to
    // still line up
    margin-top: ${(props) => (!props?.proPill ? PRO_PILL_HEIGHT : 0)};
  }
`;

CardWrapper.propTypes = {
  imgHeight: PropTypes.number.isRequired,
  proPill: PropTypes.bool,
};

CardWrapper.defaultProps = {
  proPill: false,
};

export const TopImage = styled('div')`
  display: none;

  @media ${(props) => props.theme.queries.desktopAndUp} {
    display: block;
    // hide on mobile, display carousel instead
    grid-row: TopImage / TopImageEnd;
    position: absolute;
    width: 100%;
    height: 100%;
    // align image so middle is always at center - allows for lining up of images with scaling
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const TopImageCarousel = styled('div')`
  display: block;
  width: 100%;
  @media ${(props) => props.theme.queries.desktopAndUp} {
    display: none;
  }
`;

export const CardContent = styled('div')`
  position: relative;
  grid-row-start: TopImageEnd;
  grid-row-end: CardEnd;
`;

export const CardContentDescription = styled('div')`
  padding: 0 12px;

  @media ${(props) => props.theme.queries.desktopAndUp} {
    padding: 0 24px;
  }

  svg {
    width: 100%;
  }
`;

export const CardBackground = styled('div')`
  background: white;
  border-radius: 6px;
  box-shadow: ${(props) => props.theme.effects.boxShadow.cards};
  grid-row-start: CardStart;
  grid-row-end: CardEnd;
  position: absolute;
  width: 100%;
  height: 100%;
`;

export const CenteredParagraph = styled(Paragraph)`
  text-align: center;
  color: ${(props) => props.theme.colors.slate.base};
  line-height: 24px;
  margin-bottom: 16px;

  display: ${(props) => (props.mobile ? 'block' : 'none')};

  @media ${(props) => props.theme.queries.desktopAndUp} {
    /* Allow responsive sizing when columns get skinnier on smaller desktop */
    min-height: calc(4 * 24px);
    justify-content: center;
    align-items: center;

    display: ${(props) => (props.mobile ? 'none' : 'flex')};
  }
`;

export const CenteredReferralText = styled(CenteredParagraph)`
  && {
    min-height: unset;
    display: flex;
    flex-direction: column;
  }
`;

export const ReferralTextWrapper = styled('div')`
  display: ${(props) => (props.hasReferral ? 'block' : 'none')};

  @media ${(props) => props.theme.queries.desktopAndUp} {
    // In order to keep columns lined up if only one of the products,
    // we're just going to hide the section vs not displaying it
    // for desktop
    visibility: ${(props) => (props.hasReferral ? 'inherit' : 'hidden')};
    display: block;
  }
`;

ReferralTextWrapper.propTypes = {
  hasReferral: PropTypes.bool,
};

ReferralTextWrapper.defaultProps = {
  hasReferral: false,
};

export const ButtonWrapper = styled('div')`
  margin: 24px 0;

  @media ${(props) => props.theme.queries.desktopAndUp} {
    display: flex;
    justify-content: center;
    margin: 24px 0;
  }
`;
export const ProPill = styled('div')`
  ${getTypographyVariant('header4')}
  --font-size-min: 12px;
  --font-size-max: 12px;
  --font-weight: normal;
  --letter-spacing: -0.6px;
  background-color: ${(props) => props.theme.colors.macaroon.base};
  border-radius: 10em;
  border: none;
  color: ${(props) => props.theme.colors.white};
  padding: 6px 12px;
`;

export const ProPillWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${PRO_PILL_HEIGHT};
`;

export const LogoSVG = styled(SVG)`
  margin-bottom: 12px;
  && text {
    font-family: 'Space Grotesk', sans-serif;
  }
`;

export const CenteredPrice = styled(Header4)`
  text-align: center;
  margin-bottom: 12px;
  --font-weight: normal;

  ${(props) =>
    props.strikeThrough &&
    `
    text-decoration: line-through;
    color: ${props.theme.colors.slate.lightest};
  `}
`;

CenteredPrice.propTypes = {
  hasReferral: PropTypes.bool,
};

CenteredPrice.defaultProps = {
  hasReferral: false,
};

export const ComparisonRow = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

export const ProductComparisons = styled('div')`
  display: none;
  @media ${(props) => props.theme.queries.desktopAndUp} {
    display: grid;
    background-color: ${(props) => props.theme.colors.slate.base};
    gap: 1px;
    grid-template-rows: repeat(auto, 1fr);
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    overflow: hidden;

    > ${ComparisonRow}:nth-child(even) {
      background-color: ${(props) => props.theme.colors.white};
    }
    > ${ComparisonRow}:nth-child(odd) {
      background-color: ${(props) => props.theme.colors.backgrounds.gray};
    }
  }
`;

export const ComparisonName = styled(Paragraph)`
  font-weight: normal;
  font-size: 16px;
  letter-spacing: -0.4px;
  color: ${(props) => props.theme.colors.slate.lightest};
  text-align: center;
  margin-bottom: 8px;
`;

export const ComparisonText = styled(Header4)`
  font-weight: normal;
  ${(props) =>
    props.blank ? `color: ${props.theme.colors.slate.lightest}` : ''};
`;

export const ComparisonBoolean = styled('div')`
  height: ${(props) => (props.blank ? '24px' : 'auto')};
`;

ComparisonBoolean.propTypes = {
  blank: PropTypes.bool,
};
ComparisonBoolean.defaultProps = {
  blank: false,
};

export const ComparisonValue = styled('div')``;

export const ScheduleTimeWrapper = styled('div')`
  text-align: center;
  p {
    --font-size-min: 14px;
    --font-size-max: 16px;
  }
  a {
    ${getTypographyVariant('paragraph')}
    --font-size-min: 14px;
    --font-size-max: 16px;
  }

  margin-bottom: 32px;
`;

export const ComparisonTableWithImagesStyled = styled(
  GridContainerWithBackground
)`
  background-color: ${(props) => props.theme.colors.backgrounds.gray};
  width: 100%;

  ${Title} {
    text-align: left;
    margin-bottom: 40px;
    grid-column: main-start / main-end;
    width: 16ch;
    max-width: 100%;
    @media ${(props) => props.theme.queries.desktopAndUp} {
      margin-bottom: 68px;
    }
  }
`;

export const ProductsWrapper = styled('div')`
  display: grid;
  gap: 50px;
  grid-column: main-start / main-end;

  @media ${(props) => props.theme.queries.desktopAndUp} {
    gap: 16px;
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledCheckMark = styled(SVG)``;

export const ContentWrapper = styled('div')`
  grid-column: main-start / main-end;
  padding: 60px 0;
  @media ${(props) => props.theme.queries.desktopAndUp} {
    padding: 90px 0;
  }
`;
export const CompareAllButtonWrapper = styled('div')`
  margin-top: 60px;

  @media ${(props) => props.theme.queries.desktopAndUp} {
    display: flex;
    justify-content: center;
    margin-top: 72px;
  }
`;

export const ChatWithExpertLink = styled('div')``;

/**
 * Todo: Make this carousel work right - images are overflowing??
 */
export const CarouselWrapper = styled('div')`
  position: relative;
  height: 300px;
  margin-bottom: 10px;

  > div {
    width: 100%;
    height: 100%;
  }
`;

export const CarouselImage = styled(Image)`
  display: block;
  object-fit: contain;
`;

export const CarouselImageInnerWrapper = styled('div')`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const CarouselImageWrapper = styled('div')`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const PaginationDotsContainer = styled('div')`
  display: flex;
  justify-content: center;
`;
